/**
 * 音频播放器 Vuex Module
 * 管理音频播放器的全局状态
 */

import { updatePlayStatus, updatePlayCount } from "@/api/course";
import { formatTime } from "@/utils/common";

const audioModule = {
  namespaced: true,

  state: {
    isShowPlayDialog: false, //是否展示播放列表弹窗

    // 当前播放音乐信息
    currentTrack: null,
    // 播放状态
    isPlaying: false,
    // 加载状态
    isLoading: false,
    // 播放模式：顺序播放
    playMode: "sequential",
    // 播放进度相关
    currentTime: 0,
    duration: 0,
    // 播放设置
    playbackRate: 1.0, // 播放倍速
    volume: 1.0, // 音量
    // 播放列表
    playlist: [],
    currentIndex: 0, // 当前播放索引
    // 播放列表的父对象信息（课程/专栏等信息）
    playlistInfo: null,
    // 定时播放设置
    sleepTimer: {
      enabled: false,
      duration: 0, // 分钟
      endTime: null,
      remaining: 0 // 剩余时间（毫秒）
    },
    // 定时器更新间隔ID
    sleepTimerInterval: null,
    // 播放进度同步定时器
    progressSyncInterval: null,
    // 最后一次同步的时间戳
    lastSyncTime: 0
  },

  mutations: {
    SET_IS_SHOW_PLAY_DIALOG(state, isShow) {
      state.isShowPlayDialog = isShow;
    },

    SET_CURRENT_TRACK(state, track) {
      state.currentTrack = track;
    },

    SET_PLAYING_STATE(state, isPlaying) {
      state.isPlaying = isPlaying;
    },

    SET_LOADING_STATE(state, isLoading) {
      state.isLoading = isLoading;
    },

    SET_PLAY_MODE(state, mode) {
      state.playMode = mode;
    },

    SET_CURRENT_TIME(state, time) {
      state.currentTime = time;
    },

    SET_DURATION(state, duration) {
      state.duration = duration;
    },

    SET_PLAYBACK_RATE(state, rate) {
      state.playbackRate = rate;
    },

    SET_VOLUME(state, volume) {
      state.volume = volume;
    },

    SET_PLAYLIST(state, playlist) {
      state.playlist = playlist;

      // 智能处理索引：如果当前曲目还在新列表中，保持索引；否则重置
      if (state.currentTrack) {
        const newIndex = playlist.findIndex(
          track => track.customId === state.currentTrack.customId
        );
        if (newIndex !== -1) {
          // 当前曲目还在新列表中，更新索引到新位置
          state.currentIndex = newIndex;
          console.log(`[SET_PLAYLIST] 保持当前曲目，更新索引为: ${newIndex}`);
        } else {
          // 当前曲目不在新列表中，重置索引
          state.currentIndex = 0;
          console.log("[SET_PLAYLIST] 当前曲目不在新列表，重置索引为 0");
        }
      } else {
        // 没有当前曲目，重置索引并设置第一首为当前曲目
        state.currentIndex = 0;
        if (playlist.length > 0) {
          state.currentTrack = playlist[0];
          console.log("[SET_PLAYLIST] 设置第一首为当前曲目");
        }
      }
    },

    SET_PLAYLIST_INFO(state, info) {
      state.playlistInfo = info;
    },

    // 播放列表只支持替换和清空，不支持单个添加/删除

    SET_CURRENT_INDEX(state, index) {
      state.currentIndex = index;
    },

    SET_SLEEP_TIMER(state, { enabled, duration, endTime }) {
      state.sleepTimer = {
        enabled: enabled ?? state.sleepTimer.enabled,
        duration: duration ?? state.sleepTimer.duration,
        endTime: endTime ?? state.sleepTimer.endTime,
        remaining: state.sleepTimer.remaining
      };
    },

    SET_SLEEP_TIMER_REMAINING(state, remaining) {
      state.sleepTimer.remaining = remaining;
    },

    SET_SLEEP_TIMER_INTERVAL(state, intervalId) {
      // 清除现有的定时器
      if (state.sleepTimerInterval) {
        clearInterval(state.sleepTimerInterval);
      }
      state.sleepTimerInterval = intervalId;
    },

    SET_PROGRESS_SYNC_INTERVAL(state, intervalId) {
      // 清除现有的定时器
      if (state.progressSyncInterval) {
        clearInterval(state.progressSyncInterval);
      }
      state.progressSyncInterval = intervalId;
    },

    SET_LAST_SYNC_TIME(state, timestamp) {
      state.lastSyncTime = timestamp;
    },

    // 更新当前播放进度到本地（同步currentTrack和playlist）
    UPDATE_LOCAL_PROGRESS(state, progress) {
      // 更新currentTrack的progress
      if (state.currentTrack) {
        state.currentTrack.progress = progress;
      }

      // 更新playlist中匹配的track的progress
      if (state.currentTrack && state.playlist.length > 0) {
        const track = state.playlist[state.currentIndex];
        if (track && track.id === state.currentTrack.id) {
          track.progress = progress;
        }
      }
    },

    // 清空播放列表
    CLEAR_PLAYLIST(state) {
      // 清理播放进度同步定时器，防止内存泄漏
      if (state.progressSyncInterval) {
        clearInterval(state.progressSyncInterval);
        state.progressSyncInterval = null;
      }
      // 清理睡眠定时器
      if (state.sleepTimerInterval) {
        clearInterval(state.sleepTimerInterval);
        state.sleepTimerInterval = null;
      }
      state.playlist = [];
      state.currentIndex = 0;
      state.currentTrack = null;
      state.playlistInfo = null;
    }
  },

  actions: {
    // 设置是否展示播放列表弹窗
    setShowPlayDialog({ commit }, isShow) {
      commit("SET_IS_SHOW_PLAY_DIALOG", isShow);
    },
    // 设置当前曲目（不改变播放状态）
    setCurrentTrack({ commit, state }, { track, index }) {
      const isSameTrack =
        state.currentTrack && state.currentTrack.customId === track.customId;
      console.log(
        "[Audio.js,setCurrentTrack]",
        state.currentTrack.customId,
        track.customId
      );
      // 设置索引
      if (index !== undefined) {
        commit("SET_CURRENT_INDEX", index);
      }

      // 设置当前音乐
      commit("SET_CURRENT_TRACK", track);

      // 只有当切换到不同音乐时才重置播放时间
      if (!isSameTrack) {
        commit("SET_CURRENT_TIME", 0);
      }
    },

    /**
     * 播放指定音乐（仅更新Store状态，不操作硬件）
     * @param {Object} track - 音轨对象
     * @param {Number} index - 播放列表索引
     */
    async playTrack({ commit }, { track, index }) {
      console.log("[Store/playTrack] 更新Store状态");
      console.log("[Store/playTrack] Track:", track.title || track.id);
      console.log("[Store/playTrack] Index:", index);

      // 设置索引
      if (index !== undefined) {
        commit("SET_CURRENT_INDEX", index);
      }

      // 设置当前音乐
      commit("SET_CURRENT_TRACK", track);

      // 设置播放状态为 true（会被audioMixin重置为false，等待硬件真正开始播放）
      commit("SET_PLAYING_STATE", true);
    },

    // 播放/暂停
    togglePlay({ commit, state }) {
      commit("SET_PLAYING_STATE", !state.isPlaying);
    },

    // 设置加载状态
    setLoadingState({ commit }, isLoading) {
      commit("SET_LOADING_STATE", isLoading);
    },

    // 上一首
    playPrevious({ state, dispatch }) {
      const { playlist, currentIndex } = state;
      if (playlist.length === 0 || currentIndex <= 0) return;

      const newIndex = currentIndex - 1;
      const track = playlist[newIndex];
      if (track) {
        dispatch("playTrack", { track, index: newIndex });
      }
    },

    // 下一首（顺序播放）
    playNext({ state, dispatch, commit }) {
      const { playlist, currentIndex } = state;
      if (playlist.length === 0) return;

      // 如果只有一首歌，播放完后重置并暂停
      if (playlist.length === 1) {
        commit("SET_PLAYING_STATE", false);
        commit("SET_CURRENT_TIME", 0);
        return;
      }

      // 如果已经是最后一首，不执行任何操作
      if (currentIndex >= playlist.length - 1) {
        return;
      }

      // 正常播放下一首
      const newIndex = currentIndex + 1;
      const track = playlist[newIndex];
      if (track) {
        dispatch("playTrack", { track, index: newIndex });
      }
    },

    // 设置播放列表（替换全部）
    setPlaylist({ commit }, { playlist, info }) {
      if (playlist && playlist.length > 0) {
        commit("SET_PLAYLIST", playlist);
      }
      if (info) {
        commit("SET_PLAYLIST_INFO", info);
      }
    },

    // 清空播放列表
    clearPlaylist({ commit }) {
      commit("CLEAR_PLAYLIST");
    },

    // 播放列表中的指定音乐
    playFromPlaylist({ dispatch, state }, index) {
      if (index >= 0 && index < state.playlist.length) {
        const track = state.playlist[index];
        dispatch("playTrack", { track, index });
      }
    },

    // 播放模式已固定为顺序播放，不需要切换功能

    // 设置播放倍速
    setPlaybackRate({ commit }, rate) {
      if (rate >= 0.5 && rate <= 2.0) {
        commit("SET_PLAYBACK_RATE", rate);
      }
    },

    // 设置音量
    setVolume({ commit }, volume) {
      if (volume >= 0 && volume <= 1) {
        commit("SET_VOLUME", volume);
      }
    },

    // 跳转到指定时间
    seekTo({ commit }, time) {
      if (time >= 0) {
        commit("SET_CURRENT_TIME", time);
      }
    },

    // 启用定时播放
    setSleepTimer({ commit, dispatch }, minutes) {
      const endTime = Date.now() + minutes * 60 * 1000;
      // 初始剩余时间也应该基于 endTime 计算，保证与后续更新逻辑一致
      const remaining = endTime - Date.now();

      commit("SET_SLEEP_TIMER", {
        enabled: true,
        duration: minutes,
        endTime
      });

      commit("SET_SLEEP_TIMER_REMAINING", remaining);

      // 启动定时器更新剩余时间
      dispatch("startSleepTimerUpdate", endTime);
    },

    // 取消定时播放
    cancelSleepTimer({ commit, dispatch }) {
      // 清除定时器更新间隔
      dispatch("stopSleepTimerUpdate");

      commit("SET_SLEEP_TIMER", {
        enabled: false,
        duration: 0,
        endTime: null
      });

      commit("SET_SLEEP_TIMER_REMAINING", 0);
    },

    // 开始定时器更新
    startSleepTimerUpdate({ commit, dispatch }, endTime) {
      // 清除现有的定时器
      dispatch("stopSleepTimerUpdate");

      // 每秒更新一次剩余时间
      const intervalId = setInterval(() => {
        const remaining = Math.max(0, endTime - Date.now());
        commit("SET_SLEEP_TIMER_REMAINING", remaining);

        // 如果剩余时间为0，停止定时器并处理结束逻辑
        if (remaining === 0) {
          dispatch("handleSleepTimerEnd");
        }
      }, 1000);

      commit("SET_SLEEP_TIMER_INTERVAL", intervalId);
    },

    // 停止定时器更新
    stopSleepTimerUpdate({ commit, state }) {
      if (state.sleepTimerInterval) {
        clearInterval(state.sleepTimerInterval);
        commit("SET_SLEEP_TIMER_INTERVAL", null);
      }
    },

    // 处理定时器结束
    handleSleepTimerEnd({ dispatch, commit, state }) {
      console.log("Sleep timer ended");

      // 如果当前正在播放，暂停播放
      if (state.isPlaying) {
        commit("SET_PLAYING_STATE", false);
      }

      // 清理定时器状态
      dispatch("cancelSleepTimer");
    },

    // 同步播放进度到服务器
    // @param {Object} options - 可选参数
    // @param {Object} options.track - 指定要同步的音轨（可选，默认使用 state.currentTrack）
    // @param {Number} options.time - 指定要同步的播放时间（可选，默认使用 state.currentTime）
    // @param {Object} options.playlistInfo - 指定播放列表信息（可选，默认使用 state.playlistInfo）
    // @param {Number} options.type - 指定资源类型（可选，优先使用 playlistInfo.type，否则使用 state.playlistInfo.type）
    async syncPlayProgress({ state, commit }, options = {}) {
      // 检查用户是否已登录
      const isLoggedIn = localStorage.getItem("token");
      if (!isLoggedIn) {
        console.log("用户未登录，跳过播放进度同步");
        return;
      }

      // 使用传入的参数或从 state 获取
      const track = options.track || state.currentTrack;
      const time =
        options.time !== undefined ? options.time : state.currentTime;
      const playlistInfo = options.playlistInfo || state.playlistInfo;
      // 优先使用传入的 type，否则使用 playlistInfo.type
      const type =
        options.type !== undefined
          ? options.type
          : playlistInfo
            ? playlistInfo.type
            : null;

      // 检查是否有当前播放音频和播放列表信息
      if (!track || !playlistInfo || !type) {
        console.log("无法同步播放进度：缺少必要信息");
        return;
      }

      // 检查是否需要付费，需要付费的章节不同步进度
      if (track.isNeedPay) {
        console.log("当前章节需要付费，跳过播放进度同步");
        return;
      }

      try {
        // 准备同步参数
        const params = {
          id: track.fId, // 课程/有声书ID
          chapterId: track.id, // 章节ID
          progress: Math.floor(time), // 播放进度（秒），向下取整
          type: type // 资源类型 (1=有声书, 2=课程)
        };

        console.log("同步播放进度:", params);

        // 调用接口同步
        const response = await updatePlayStatus(params);

        if (response && response.code === 0) {
          // 更新最后同步时间
          commit("SET_LAST_SYNC_TIME", Date.now());
          console.log("播放进度同步成功");
        } else {
          console.warn("播放进度同步失败:", response);
        }
      } catch (error) {
        // 静默处理错误，不影响播放流程
        // Token过期等错误会由request.js统一处理（清空登录信息、跳转首页）
        console.error("同步播放进度出错:", error);
        // 不要抛出错误，避免影响播放
      }
    },

    // 开始播放进度定时同步（每1分钟）
    startProgressSync({ commit, dispatch, state }) {
      // 清除现有的定时器
      dispatch("stopProgressSync");

      // 每60秒（1分钟）同步一次
      const intervalId = setInterval(() => {
        // 只在播放状态下同步
        if (state.isPlaying && state.currentTrack) {
          dispatch("syncPlayProgress");
        }
      }, 60000); // 60000毫秒 = 1分钟

      commit("SET_PROGRESS_SYNC_INTERVAL", intervalId);
      console.log("播放进度定时同步已启动（每1分钟）");
    },

    // 停止播放进度定时同步
    stopProgressSync({ commit, state }) {
      if (state.progressSyncInterval) {
        clearInterval(state.progressSyncInterval);
        commit("SET_PROGRESS_SYNC_INTERVAL", null);
        console.log("播放进度定时同步已停止");
      }
    },

    // 同步播放次数到服务器（播放时调用一次）
    async syncPlayCount({ state }) {
      // 检查用户是否已登录
      const isLoggedIn = localStorage.getItem("token");
      if (!isLoggedIn) {
        console.log("用户未登录，跳过播放次数同步");
        return;
      }

      const { currentTrack, playlistInfo } = state;

      // 检查是否有当前播放音频和播放列表信息
      if (!currentTrack || !playlistInfo) {
        console.log("无法同步播放次数：缺少必要信息");
        return;
      }

      // 检查是否需要付费，需要付费的章节不同步播放次数
      if (currentTrack.isNeedPay) {
        console.log("当前章节需要付费，跳过播放次数同步");
        return;
      }

      try {
        // 准备同步参数
        const params = {
          id: currentTrack.fId, // 课程/有声书ID
          chapterId: currentTrack.id, // 章节ID
          type: playlistInfo.type // 资源类型 (1=有声书, 2=课程)
        };

        console.log("同步播放次数:", params);

        // 调用接口同步
        const response = await updatePlayCount(params);

        if (response && response.code === 0) {
          console.log("播放次数同步成功");
        } else {
          console.warn("播放次数同步失败:", response);
        }
      } catch (error) {
        // 静默处理错误，不影响播放流程
        // Token过期等错误会由request.js统一处理（清空登录信息、跳转首页）
        console.error("同步播放次数出错:", error);
        // 不要抛出错误，避免影响播放
      }
    }
  },

  getters: {
    // 是否展示播放列表弹窗
    isShowPlayDialog: state => state.isShowPlayDialog,
    // 获取当前播放的音乐
    currentTrack: state => state.currentTrack,

    // 获取播放状态
    isPlaying: state => state.isPlaying,

    // 获取加载状态
    isLoading: state => state.isLoading,

    // 判断是否可以播放（未加载中且有音频）
    canPlay: state => !state.isLoading && state.currentTrack !== null,

    // 获取播放模式
    playMode: state => state.playMode,

    // 获取播放进度百分比
    playProgress: state => {
      // 优先从当前音频数据中获取时长，如果没有再用实际播放的时长
      const duration =
        state.currentTrack && state.currentTrack.duration
          ? state.currentTrack.duration
          : state.duration;
      if (duration === 0) return 0;
      return (state.currentTime / duration) * 100;
    },

    // 获取格式化的时间
    formattedCurrentTime: state => formatTime(state.currentTime),

    formattedDuration: state => {
      // 优先从当前音频数据中获取时长，如果没有再用实际播放的时长
      const duration =
        state.currentTrack && state.currentTrack.duration
          ? state.currentTrack.duration
          : state.duration;
      return formatTime(duration);
    },

    // 是否有播放列表
    hasPlaylist: state => state.playlist.length > 0,

    // 播放列表长度
    playlistLength: state => state.playlist.length,

    // 获取播放列表
    playlist: state => state.playlist,

    // 获取播放列表父对象信息
    playlistInfo: state => state.playlistInfo,

    // 获取当前播放索引
    currentIndex: state => state.currentIndex,

    // 获取播放设置
    playbackRate: state => state.playbackRate,

    volume: state => state.volume,

    // 定时播放相关
    sleepTimer: state => state.sleepTimer,

    // 定时播放剩余时间（毫秒）
    sleepTimerRemaining: state => {
      if (!state.sleepTimer.enabled) {
        return 0;
      }
      return state.sleepTimer.remaining;
    },

    // 定时播放剩余时间（格式化）
    formattedSleepTimerRemaining: state => {
      if (!state.sleepTimer.enabled || state.sleepTimer.remaining === 0) {
        return "00:00";
      }

      // 将毫秒转换为秒，使用 ceil 向上取整避免跳秒
      // 例如：1800000ms -> 1800s -> 30:00
      //       1799001ms -> 1800s -> 30:00（向上取整）
      //       1799000ms -> 1799s -> 29:59
      const seconds = Math.ceil(state.sleepTimer.remaining / 1000);
      return formatTime(seconds);
    },

    // 是否可以播放上一首（第一首时禁用）
    canPlayPrevious: state =>
      state.playlist.length > 1 && state.currentIndex > 0,

    // 是否可以播放下一首（最后一首时禁用）
    canPlayNext: state =>
      state.playlist.length > 1 &&
      state.currentIndex < state.playlist.length - 1,

    // 播放模式文本（固定为顺序播放）
    playModeText: () => "顺序播放",

    // 播放模式图标（固定图标）
    playModeIcon: () => "reload"
  }
};

export default audioModule;
